// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { api } from '../../api/client';
import type { SolidOpening, SolidWall } from '../../api/types';
import { isTerminal } from '../../api/types';
import { useJobs } from '../../app/JobsContext';
import { useAsync } from '../../app/useAsync';

export interface BuildingModel {
  /** `GET /walls`; null until read, or on a server without the route. */
  walls: SolidWall[] | null;
  /** `GET /openings`; null until read, or on a server without the route. */
  openings: SolidOpening[] | null;
}

/** A read whose failure (an older server's 404 included) is simply "not known". */
async function orNull<T>(run: () => Promise<T>, signal: AbortSignal): Promise<T | null> {
  try {
    return await run();
  } catch (cause) {
    if (signal.aborted) throw cause;
    return null;
  }
}

/**
 * The building model's walls and openings (ruxd#11) for the plan's wall
 * layer and the measure tools' and Placér 360°'s snapping (frontend-4rt).
 * Re-read when a job finishes (an import writes them) and on `model.changed`
 * (an edit), like the spaces in `useServerSpatial`.
 */
export function useBuildingModel(): BuildingModel {
  const { jobs, modelChange } = useJobs();
  const finished = jobs.filter((j) => isTerminal(j.status)).length;
  // A model edit (Rediger › Geometri, ruxd-5uv) rewrites rows without a job.
  const edited = modelChange?.revision ?? null;
  const wallsRead = useAsync((signal) => orNull(() => api.walls(signal), signal), [finished, edited]);
  const openingsRead = useAsync((signal) => orNull(() => api.openings(signal), signal), [finished, edited]);
  return { walls: wallsRead.data ?? null, openings: openingsRead.data ?? null };
}
